import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/useAuth";

export type TenantDeletionStatus = "Pending" | "Running" | "Failed";

export interface TenantDeletionRequestInfo {
  status: TenantDeletionStatus;
  source: "Tenant" | "PlatformAdmin";
  requestedAt: string;
  effectiveAt: string;
  requestedByName?: string | null;
}

export interface TenantDeletionState {
  tenantName: string;
  request: TenantDeletionRequestInfo | null;
  canRequest: boolean;
  blockedReasonKey?: string | null;
  canManage: boolean;
  waitingPeriodDays: number;
}

const TENANT_DELETION_QUERY_KEY = ["tenant-deletion"] as const;
const ENDPOINT = "/api/v1/settings/tenant-deletion";

/**
 * Löschantrag des eigenen Mandanten. Eine Quelle für die Einstellungsseite und den Hinweis auf
 * jeder Seite — beide lesen denselben Abfrageschlüssel, damit Antrag und Widerruf überall sofort
 * sichtbar werden.
 */
export function useTenantDeletionState(enabled = true) {
  const { api } = useAuth();
  return useQuery({
    queryKey: TENANT_DELETION_QUERY_KEY,
    enabled,
    queryFn: () => api.get<TenantDeletionState>(ENDPOINT, { suppressGlobalErrorToast: true }),
  });
}

export function useTenantDeletionActions() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: TENANT_DELETION_QUERY_KEY });

  const request = useMutation({
    mutationFn: (values: { companyName: string; password: string }) =>
      api.post<void>(`${ENDPOINT}/request`, values, { suppressGlobalErrorToast: true }),
    onSuccess: refresh,
  });
  const revoke = useMutation({
    mutationFn: () => api.post<void>(`${ENDPOINT}/revoke`, {}),
    onSuccess: refresh,
  });
  return { request, revoke };
}
